import { get } from "lodash";
import { Box, Flex, FlexItem, Button, P } from "orcs-design-system";
import React from "react";

import TagBadge from "src/components/Badge/TagBadge";

import { TAG_OPERATIONS } from "src/consts/tags";
import { isTagNavigableInTeamform } from "src/util/isTagNavigableInTeamform";
import { shouldShowTagChipEditButton } from "../TagsEditor.util";
import { shouldHighlightTag } from "../utils/shouldHighlightTag";
import TagTypeHelpButton from "./TagTypeHelpButton";

const actionButtonText = (option) => {
  if (
    get(option, "data.tag.tagTypeConfig.allowMultipleOnEntity") ||
    get(option, "existingTagsOfType") === 0
  ) {
    return "Add";
  }

  return "Replace";
};

const formatOptionLabel = (
  option,
  {
    context,
    showTagTypeHelp,
    onRemoveTag,
    onEditTag,
    onAddTag,
    onTagClick,
    tagsUpdating = {},
    entityType,
  }
) => {
  const { data } = option;
  if (!data) {
    return null;
  }

  const {
    tag: { tagTypeConfig, ...tag },
  } = data;

  const { name: tagTypeName, isReadonly } = tagTypeConfig;
  const getTypeName = () => tagTypeName;

  if (context === "value") {
    const isEditable = shouldShowTagChipEditButton(tagTypeConfig);

    const onRemove = (e) => {
      e.stopPropagation();
      onRemoveTag(tag, tagTypeConfig);
    };

    const onEdit = (e) => {
      e.stopPropagation();
      onEditTag(tag, tagTypeConfig);
    };

    const tagWithConfig = { ...tag, tagTypeConfig };
    const canNavigate = isTagNavigableInTeamform(tagWithConfig);

    const onSelect = (e) => {
      e.stopPropagation();
      if (!canNavigate) {
        return;
      }
      onTagClick(tagWithConfig, tagTypeConfig);
    };

    const shouldHightlight = shouldHighlightTag({
      entityType,
      tag: {
        tagTypeConfig,
      },
    });

    const showUpdating = get(tagsUpdating, `[${tag?.id}]`, false);
    if (tag.status === TAG_OPERATIONS.REMOVED) {
      return (
        <TagBadge
          value={tag}
          getTypeName={getTypeName}
          withType
          selected={false}
          disabled
          isRemoved
          highlighted={shouldHightlight}
          tagTypeConfig={tagTypeConfig}
        />
      );
    }

    return (
      <TagBadge
        value={tag}
        getTypeName={getTypeName}
        withType
        selected={canNavigate}
        onSelect={canNavigate ? onSelect : undefined}
        showEdit={isEditable}
        showRemove={!isReadonly}
        onEdit={onEdit}
        onRemove={onRemove}
        showUpdating={showUpdating}
        highlighted={shouldHightlight}
        tagTypeConfig={tagTypeConfig}
      />
    );
  }

  if (tag.isUserCreatedTagValue) {
    return (
      <Flex alignItems="center" justifyContent="space-between" gap="s">
        <FlexItem flex="1 1 auto">
          <P>{`Create new ${tagTypeName} tag`}</P>
        </FlexItem>
        <FlexItem ml="auto">
          {tagTypeConfig.hasDetails && (
            <TagTypeHelpButton
              tagTypeConfig={tagTypeConfig}
              displayTagTypeHelp={showTagTypeHelp}
            />
          )}
          <Button small variant="success">
            Create
          </Button>
        </FlexItem>
      </Flex>
    );
  }

  const onSelect = (e) => {
    e.stopPropagation();
    onAddTag(tag, tagTypeConfig);
  };

  return (
    <Box bg="transparent" width="100%">
      <Flex
        justifyContent="space-between"
        width="100%"
        alignItems="center"
        gap="s"
      >
        <FlexItem onClick={onSelect}>
          <TagBadge
            value={tag}
            getTypeName={getTypeName}
            tagTypeConfig={tagTypeConfig}
            withType
          />
        </FlexItem>
        <Flex alignItems="center" gap="s" ml="auto">
          {tagTypeConfig.hasDetails && (
            <TagTypeHelpButton
              tagTypeConfig={tagTypeConfig}
              displayTagTypeHelp={showTagTypeHelp}
            />
          )}
          <Button small>{actionButtonText(option)}</Button>
        </Flex>
      </Flex>
    </Box>
  );
};

export default formatOptionLabel;
